import { Check, ChevronDown, Eye, FileText, Flag, Globe2, Image, Layers, Maximize2, Minimize2, Monitor, PanelLeftClose, PanelLeftOpen, Plus, Search, Send, ShieldCheck, Smartphone, Tablet, Undo2, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RichTextEditor } from "./RichTextEditor";
import { Button, Tag } from "./ui";

type PublicContentType = "Page" | "News" | "Current action";
type PublicContentStatus = "published" | "draft" | "pending approval";
type ApprovalStatus = "pending" | "approved" | "rejected";
type ContentFilter = "All" | "Pages" | "Actions" | "News";
type PreviewDevice = "desktop" | "tablet" | "mobile";

const previewDeviceWidths: Record<PreviewDevice, number | null> = { desktop: null, tablet: 834, mobile: 390 };
const previewDeviceOptions: { key: PreviewDevice; label: string; icon: typeof Monitor }[] = [
  { key: "desktop", label: "Desktop", icon: Monitor },
  { key: "tablet", label: "Tablet", icon: Tablet },
  { key: "mobile", label: "Mobile", icon: Smartphone },
];

const editorWidthStorageKey = "lexmosaic.publicSiteEditorWidth";
const defaultEditorWidth = 460;

type PublicContentItem = {
  id: string;
  itemKey: string;
  type: PublicContentType;
  title: string;
  slug: string;
  path: string;
  summary: string;
  body: string;
  imageUrl?: string;
  status: PublicContentStatus;
  updatedAt: number;
  tags?: string[];
  publicDate?: string | null;
  actionStatus?: string | null;
  lastPublishedAt?: number | null;
  publishError?: string | null;
};

type PublicSiteEditorData = {
  items: Array<{
    id: string;
    itemKey: string;
    contentType: PublicContentType;
    title: string;
    slug: string;
    path: string;
    summary: string;
    body: string;
    imageUrl?: string | null;
    status: PublicContentStatus;
    updatedAt: number;
    tags?: string[];
    publicDate?: string | null;
    actionStatus?: string | null;
    lastPublishedAt?: number | null;
    publishError?: string | null;
  }>;
  changes: Array<{
    id: string;
    itemKey: string;
    contentType: PublicContentType;
    title: string;
    path: string;
    submittedBy: string;
    submittedAt: number;
    reviewedAt?: number | null;
    status: ApprovalStatus;
    note: string;
    beforeJson: string;
    afterJson: string;
  }>;
  media: PublicMediaItem[];
};

type PublicSiteEditorPersistence = {
  saveDraft?: (item: PublicContentItem) => Promise<unknown>;
  submitForApproval?: (item: PublicContentItem, note?: string) => Promise<unknown>;
  reviewChange?: (changeId: string, status: Exclude<ApprovalStatus, "pending">) => Promise<unknown>;
  addMedia?: (asset: { label: string; url: string; alt: string }) => Promise<unknown>;
  retryPublish?: (itemKey: string) => Promise<unknown>;
};

type PublicMediaItem = {
  id: string;
  label: string;
  url: string;
  alt: string;
};

type PublicSiteChange = {
  id: string;
  itemId: string;
  title: string;
  type: PublicContentType;
  submittedBy: string;
  submittedAt: number;
  status: ApprovalStatus;
  note: string;
  before: PublicContentItem;
  after: PublicContentItem;
  reviewedAt?: number;
};

const publicMedia: PublicMediaItem[] = [
  { id: "media-hero-network", label: "Network mosaic", url: "https://lexmosaic.eu/images/hero-network.png", alt: "LexMosaic independent legal network visual" },
  { id: "media-membership", label: "Membership", url: "https://lexmosaic.eu/images/membership-review.png", alt: "Independent firm membership visual" },
];

const initialPublicContent: PublicContentItem[] = [
  {
    id: "page-home",
    itemKey: "page-home",
    type: "Page",
    title: "Home",
    slug: "index",
    path: "/",
    summary: "Public introduction to LexMosaic and its cross-border legal network.",
    body: "## Independent practices, connected capability\n\nLexMosaic connects independent legal practices for trusted cross-border capability, shared insight and principled cooperation.\n\n- Cross-border legal capability\n- Independent professional identity\n- Shared public legal insight",
    status: "published",
    updatedAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
  },
  {
    id: "page-about",
    itemKey: "page-about",
    type: "Page",
    title: "About LexMosaic",
    slug: "about",
    path: "/about",
    summary: "Mission, positioning and public-facing explanation of the network.",
    body: "## A European network for independent legal expertise\n\nLexMosaic is built around independent legal expertise, local judgment and carefully governed European cooperation.\n\n[View the public website](https://lexmosaic.eu)",
    status: "published",
    updatedAt: Date.now() - 4 * 24 * 60 * 60 * 1000,
  },
  {
    id: "page-membership",
    itemKey: "page-membership",
    type: "Page",
    title: "Membership",
    slug: "membership",
    path: "/membership",
    summary: "Who can join and the membership pathway.",
    body: "## Becoming a member\n\nMembership is open to independent firms that meet the network charter.\n\n### What LexMosaic looks for\n\n- Independent legal judgment\n- Cross-border cooperation capability\n- Commitment to shared professional standards",
    status: "draft",
    updatedAt: Date.now() - 3 * 24 * 60 * 60 * 1000,
  },
  {
    id: "action-public-register",
    itemKey: "action-public-register",
    type: "Current action",
    title: "Public actions register",
    slug: "public-actions-register",
    path: "/actions/public-actions-register",
    summary: "A transparent register of public-facing LexMosaic actions.",
    body: "## Public-facing work\n\nLexMosaic publishes selected public actions where the network can contribute independent legal insight to European questions.",
    status: "draft",
    updatedAt: Date.now() - 2 * 24 * 60 * 60 * 1000,
  },
  {
    id: "news-public-launch",
    itemKey: "news-public-launch",
    type: "News",
    title: "Public site launch",
    slug: "public-site-launch",
    path: "/news/public-site-launch",
    summary: "Announcement for the public LexMosaic website.",
    body: "## Public website launch\n\nThe public website gives external audiences a clear view of LexMosaic's mission, public actions and membership pathway.",
    status: "pending approval",
    updatedAt: Date.now() - 24 * 60 * 60 * 1000,
  },
];

export function PublicSiteEditorPanel({ viewerName, viewerRole, canApprove = viewerRole === "Super Admin", data, persistence, onNotice }: { viewerName: string; viewerRole: string; canApprove?: boolean; data?: PublicSiteEditorData; persistence?: PublicSiteEditorPersistence; onNotice: (message: string) => void }) {
  const initialItems = data && data.items.length > 0 ? data.items.map(fromServerContent) : initialPublicContent;
  const [items, setItems] = useState(initialItems);
  const [selectedId, setSelectedId] = useState(initialItems[2]?.id ?? initialItems[0]?.id ?? "");
  const [draft, setDraft] = useState(initialItems[2] ?? initialItems[0]);
  const [changes, setChanges] = useState<PublicSiteChange[]>(data && data.changes.length > 0 ? data.changes.map(fromServerChange) : [
    {
      id: "change-public-copy",
      itemId: "news-public-launch",
      title: "Public site launch",
      type: "News",
      submittedBy: "Public editor",
      submittedAt: Date.now() - 4 * 60 * 60 * 1000,
      status: "pending",
      note: "Review public launch announcement before publication.",
      before: initialPublicContent[4],
      after: { ...initialPublicContent[4], summary: "Announcement for the public LexMosaic website and membership pathway." },
    },
  ]);
  const [filter, setFilter] = useState<ContentFilter>("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewOpen, setPreviewOpen] = useState(true);
  const [contentOpen, setContentOpen] = useState(true);
  const [fullPreview, setFullPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>("desktop");
  const [editorWidth, setEditorWidth] = useState(() => readEditorWidth());
  const [resizingPreview, setResizingPreview] = useState(false);
  const resizeStart = useRef<{ x: number; width: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [approvalNote, setApprovalNote] = useState("");
  const [mediaDraft, setMediaDraft] = useState({ label: "", url: "", alt: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  const seededRef = useRef(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const newMenuRef = useRef<HTMLDivElement>(null);
  const selectedItem = items.find((item) => item.id === selectedId) ?? items[0];
  const pendingChanges = changes.filter((change) => change.status === "pending");
  const mediaItems = data?.media && data.media.length > 0 ? data.media : publicMedia;
  const previewUrl = selectedItem ? `https://lexmosaic.eu${selectedItem.path === "/" ? "" : selectedItem.path}` : "https://lexmosaic.eu";
  const counts = useMemo(() => ({
    All: items.length,
    Pages: items.filter((item) => item.type === "Page").length,
    Actions: items.filter((item) => item.type === "Current action").length,
    News: items.filter((item) => item.type === "News").length,
  }), [items]);
  const visibleItems = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    return items.filter((item) => {
      const matchesFilter =
        filter === "All" ||
        (filter === "Pages" && item.type === "Page") ||
        (filter === "Actions" && item.type === "Current action") ||
        (filter === "News" && item.type === "News");
      const matchesSearch = !normalized || `${item.title} ${item.path} ${item.summary}`.toLowerCase().includes(normalized);
      return matchesFilter && matchesSearch;
    });
  }, [filter, items, searchTerm]);

  useEffect(() => {
    if (!data) return;
    const nextItems = data.items.length > 0 ? data.items.map(fromServerContent) : initialPublicContent;
    setItems(nextItems);
    setChanges(data.changes.map(fromServerChange));
    const stillSelected = nextItems.find((item) => item.id === selectedId) ?? nextItems[0];
    if (stillSelected) {
      setSelectedId(stillSelected.id);
      setDraft(stillSelected);
    }
  }, [data]);

  useEffect(() => {
    if (!resizingPreview) return;

    const onPointerMove = (event: PointerEvent) => {
      const start = resizeStart.current;
      if (!start) return;
      // Drag the divider: moving left shrinks the editor so the preview grows.
      setEditorWidth(clampEditorWidth(start.width + (event.clientX - start.x)));
    };
    const onPointerUp = () => {
      setResizingPreview(false);
      resizeStart.current = null;
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [resizingPreview]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(editorWidthStorageKey, String(editorWidth));
  }, [editorWidth]);

  useEffect(() => {
    if (!newMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (newMenuRef.current && !newMenuRef.current.contains(event.target as Node)) setNewMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNewMenuOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [newMenuOpen]);

  function selectItem(item: PublicContentItem) {
    setSelectedId(item.id);
    setDraft(item);
  }

  async function saveDraft() {
    const updatedDraft = { ...draft, status: "draft" as const, updatedAt: Date.now() };
    setSaving(true);
    try {
      await persistence?.saveDraft?.(updatedDraft);
      setItems((current) => current.map((item) => item.id === draft.id ? updatedDraft : item));
      setDraft(updatedDraft);
      onNotice(`${draft.title} saved as a draft.`);
    } catch (error) {
      onNotice(`Draft was not saved: ${errorMessage(error)}`);
    } finally {
      setSaving(false);
    }
  }

  function createItem(type: PublicContentType) {
    const now = Date.now();
    const defaults: Record<PublicContentType, { title: string; slug: string; path: string; body: string; noticeLabel: string }> = {
      Page: {
        title: "New public page",
        slug: "new-public-page",
        path: "/new-public-page",
        body: "## New section\n\nStart writing the public page body here.",
        noticeLabel: "page",
      },
      News: {
        title: "New news item",
        slug: "new-news-item",
        path: "/news/new-news-item",
        body: "## Summary\n\nStart writing the public news body here.",
        noticeLabel: "news item",
      },
      "Current action": {
        title: "New current action",
        slug: "new-current-action",
        path: "/actions/new-current-action",
        body: "## Summary\n\nStart writing the public action body here.",
        noticeLabel: "current action",
      },
    };
    const preset = defaults[type];
    const today = new Date(now).toISOString().slice(0, 10);
    const next: PublicContentItem = {
      id: `local-public-${now}`,
      itemKey: `local-public-${now}`,
      type,
      title: preset.title,
      slug: preset.slug,
      path: preset.path,
      summary: "",
      body: preset.body,
      status: "draft",
      updatedAt: now,
      tags: [],
      publicDate: type === "Page" ? null : today,
      actionStatus: type === "Current action" ? "Drafting" : null,
    };
    setItems((current) => [next, ...current]);
    setSelectedId(next.id);
    setDraft(next);
    setFilter("All");
    setNewMenuOpen(false);
    onNotice(`New ${preset.noticeLabel} draft created.`);
  }

  // C4 — seed a draft from a legislative position deep-link (?seedTitle=&seedSummary=).
  useEffect(() => {
    if (seededRef.current) return;
    const seedTitle = searchParams.get("seedTitle");
    if (!seedTitle) return;
    seededRef.current = true;
    const seedSummary = searchParams.get("seedSummary") ?? "";
    const now = Date.now();
    const slug = (seedTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 60)) || "legislative-update";
    const seeded: PublicContentItem = {
      id: `local-public-${now}`,
      itemKey: `local-public-${now}`,
      type: "News",
      title: seedTitle,
      slug,
      path: `/news/${slug}`,
      summary: seedSummary,
      body: seedSummary ? `## Summary\n\n${seedSummary}` : "## Summary\n\nStart writing the public summary here.",
      status: "draft",
      updatedAt: now,
    };
    setItems((current) => [seeded, ...current]);
    setSelectedId(seeded.id);
    setDraft(seeded);
    setFilter("All");
    onNotice("Public-site draft seeded from a legislative position.");
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete("seedTitle");
    cleaned.delete("seedSummary");
    setSearchParams(cleaned, { replace: true });
  }, [searchParams, setSearchParams]);

  async function submitForApproval() {
    const updatedDraft = { ...draft, status: "pending approval" as const, updatedAt: Date.now() };
    const before = items.find((item) => item.id === draft.id) ?? draft;
    setSaving(true);
    try {
      await persistence?.submitForApproval?.(updatedDraft, approvalNote);
      setItems((current) => current.map((item) => item.id === draft.id ? updatedDraft : item));
      setDraft(updatedDraft);
      setChanges((current) => [
        {
          id: `change-${Date.now()}`,
          itemId: draft.id,
          title: draft.title,
          type: draft.type,
          submittedBy: viewerName,
          submittedAt: Date.now(),
          status: "pending",
          note: approvalNote || `Proposed update for ${draft.path}`,
          before,
          after: updatedDraft,
        },
        ...current,
      ]);
      setApprovalNote("");
      onNotice(`${draft.title} submitted for superadministrator approval.`);
    } catch (error) {
      onNotice(`Public content was not submitted: ${errorMessage(error)}`);
    } finally {
      setSaving(false);
    }
  }

  async function retryPublish() {
    try {
      await persistence?.retryPublish?.(draft.itemKey);
      onNotice(`Retrying GitHub publish for ${draft.title}.`);
    } catch (error) {
      onNotice(`Publish retry failed: ${errorMessage(error)}`);
    }
  }

  async function reviewChange(changeId: string, status: Exclude<ApprovalStatus, "pending">) {
    const change = changes.find((item) => item.id === changeId);
    if (!change) return;
    try {
      await persistence?.reviewChange?.(changeId, status);
      setChanges((current) => current.map((item) => item.id === changeId ? { ...item, status, reviewedAt: Date.now() } : item));
      if (status === "approved") {
        const published = { ...change.after, status: "published" as const, updatedAt: Date.now() };
        setItems((current) => current.map((item) => item.id === change.itemId ? published : item));
        if (draft.id === change.itemId) setDraft(published);
      } else {
        setItems((current) => current.map((item) => item.id === change.itemId ? { ...item, status: "draft" } : item));
      }
      onNotice(status === "approved" ? `${change.title} approved for publication.` : `${change.title} returned to the editor.`);
    } catch (error) {
      onNotice(`Review was not saved: ${errorMessage(error)}`);
    }
  }

  return (
    <div className="-mx-4 -my-4 min-h-[calc(100vh-4rem)] bg-app md:-mx-8 md:-my-6">
      <header className="border-b border-line bg-surface px-5 py-5 md:px-8">
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
          <div>
            <div className="mono flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-secondary">
              <span>Administration</span>
              <span className="text-muted">›</span>
              <span>Publishing</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <h1 className="text-[32px] font-semibold leading-none tracking-[-0.02em] text-ink">Public site</h1>
              <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">lexmosaic.eu</span>
            </div>
            <p className="mt-4 max-w-3xl text-base leading-7 text-secondary">
              Editors prepare public pages, news and current actions here. Submissions wait for superadministrator approval before the GitHub publishing connection updates the live site.
            </p>
          </div>
          <div className="flex flex-wrap items-start gap-2.5 xl:justify-end">
            <PublishingMetric label="Pending approvals" value={pendingChanges.length} />
            <PublishingMetric label="Editable items" value={items.length} />
            <div className="rounded-lg border border-divider bg-canvas/60 px-3.5 py-2.5">
              <div className="mono text-[10px] uppercase tracking-[0.16em] text-secondary">Your role</div>
              <div className="mt-1.5 flex max-w-[260px] flex-wrap gap-1.5">
                {viewerRole.split(/[·,]/).map((part) => part.trim()).filter(Boolean).map((part) => (
                  <span key={part} className="inline-flex items-center rounded-md border border-line bg-surface px-2 py-0.5 text-[12px] font-medium text-ink">{part}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div
        className="grid min-h-[calc(100vh-14.5rem)]"
        style={{
          gridTemplateColumns: !previewOpen
            ? (contentOpen ? "minmax(240px, 300px) minmax(430px, 1fr)" : "minmax(430px, 1fr)")
            : fullPreview
              ? "minmax(0, 1fr)"
              : `${contentOpen ? "minmax(240px, 288px) " : ""}${editorWidth}px minmax(440px, 1fr)`,
        }}
      >
        {contentOpen && !fullPreview ? (
        <aside className="border-b border-line bg-surface p-5 xl:border-b-0 xl:border-r">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold tracking-[-0.02em] text-ink">Content</h2>
            <div className="flex items-center gap-2">
              <div className="relative" ref={newMenuRef}>
                <Button type="button" className="inline-flex items-center gap-2" onClick={() => setNewMenuOpen((open) => !open)} aria-haspopup="menu" aria-expanded={newMenuOpen}>
                  <Plus className="h-4 w-4" />
                  New
                  <ChevronDown className="h-4 w-4" />
                </Button>
                {newMenuOpen ? (
                  <div role="menu" className="absolute right-0 top-full z-20 mt-2 w-52 overflow-hidden rounded-xl border border-line bg-surface py-1.5 shadow-[0_18px_40px_rgba(11,31,58,0.18)]">
                    {(["Page", "News", "Current action"] as PublicContentType[]).map((type) => (
                      <button key={type} type="button" role="menuitem" onClick={() => createItem(type)} className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm font-medium text-ink hover:bg-hover">
                        <ContentTypeIcon type={type} />
                        {type}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <button type="button" onClick={() => setContentOpen(false)} aria-label="Collapse content list" title="Collapse content list" className="grid h-10 w-10 place-items-center rounded-lg border border-line bg-surface text-secondary hover:bg-hover hover:text-ink">
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
          </div>

          <label className="mb-4 flex min-h-12 items-center gap-3 rounded-xl border border-line bg-canvas px-3 text-secondary">
            <Search className="h-4 w-4" />
            <input className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search content..." />
          </label>

          <div className="mb-5 grid grid-cols-4 overflow-hidden rounded-xl border border-line bg-canvas p-1">
            {(["All", "Pages", "Actions", "News"] as ContentFilter[]).map((item) => (
              <button
                key={item}
                type="button"
                className={`min-h-10 rounded-lg text-sm font-semibold ${filter === item ? "border border-primary bg-surface text-primary shadow-[0_1px_0_rgba(11,31,58,0.04)]" : "text-secondary hover:bg-surface"}`}
                onClick={() => setFilter(item)}
              >
                {item} <span className="text-muted">{counts[item]}</span>
              </button>
            ))}
          </div>

          <div className="grid gap-2">
            {visibleItems.length === 0 ? (
              <div className="rounded-xl border border-dashed border-line px-4 py-6 text-center">
                <p className="text-sm font-medium text-ink">
                  {searchTerm.trim() ? "No content matches your search." : `No ${filter === "All" ? "content" : filter.toLowerCase()} yet — create one to get started.`}
                </p>
                {!searchTerm.trim() ? <p className="mt-1 text-xs text-secondary">Use the "New" button above to add a page, news item or current action.</p> : null}
              </div>
            ) : visibleItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`group grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border-l-4 px-3 py-3 text-left ${
                  selectedId === item.id ? "border-primary bg-primary/10" : "border-transparent hover:bg-hover"
                }`}
                onClick={() => selectItem(item)}
              >
                <ContentTypeIcon type={item.type} />
                <span className="min-w-0">
                  <span className="block truncate text-base font-semibold tracking-[-0.01em] text-ink">{item.title}</span>
                  <span className="mono mt-1 block truncate text-xs text-secondary">{item.path}</span>
                </span>
                <StatusPill status={item.status} />
              </button>
            ))}
          </div>
        </aside>
        ) : null}

        {!fullPreview ? (
        <main className="min-w-0 border-b border-line xl:border-b-0 xl:border-r">
          <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-line bg-app/95 px-5 py-4 backdrop-blur md:px-8">
            <div className="flex flex-wrap items-center gap-3">
              {!contentOpen ? (
                <button type="button" onClick={() => setContentOpen(true)} aria-label="Show content list" title="Show content list" className="grid h-10 w-10 place-items-center rounded-lg border border-line bg-surface text-secondary hover:bg-hover hover:text-ink">
                  <PanelLeftOpen className="h-4 w-4" />
                </button>
              ) : null}
              <span className="mono text-sm text-secondary">{draft.path}</span>
              <StatusPill status={draft.status} />
              <PublishStatusIndicator item={draft} onRetry={retryPublish} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={saveDraft} disabled={saving}>{saving ? "Saving..." : "Save draft"}</Button>
              <Button type="button" className="inline-flex items-center gap-2" onClick={submitForApproval} disabled={saving}>
                <Upload className="h-4 w-4" />
                {saving ? "Saving..." : "Submit for approval"}
              </Button>
              {!previewOpen ? (
                <Button type="button" variant="secondary" onClick={() => setPreviewOpen(true)}>
                  <Eye className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>

          <div className="grid gap-5 px-5 py-6 md:px-8">
            <EditorField label="Title" value={draft.title} onChange={(value) => setDraft((current) => ({ ...current, title: value }))} emphasis />
            <div className="grid gap-5 md:grid-cols-[240px_minmax(0,1fr)]">
              <EditorSelect label="Type" value={draft.type} values={["Page", "News", "Current action"]} onChange={(value) => setDraft((current) => ({ ...current, type: value as PublicContentType }))} />
              <EditorField label="Slug" value={draft.slug} onChange={(value) => setDraft((current) => ({ ...current, slug: value }))} />
            </div>
            <EditorField label="Public path" value={draft.path} onChange={(value) => setDraft((current) => ({ ...current, path: value }))} />
            <EditorField label="Lead image URL" value={draft.imageUrl ?? ""} onChange={(value) => setDraft((current) => ({ ...current, imageUrl: value || undefined }))} />
            {draft.type === "News" || draft.type === "Current action" ? (
              <div className="grid gap-5 md:grid-cols-[200px_minmax(0,1fr)]">
                <EditorField label="Publication date" type="date" value={draft.publicDate ?? ""} onChange={(value) => setDraft((current) => ({ ...current, publicDate: value || null }))} />
                <EditorField
                  label="Tags"
                  placeholder="Comma-separated, e.g. Public site, Governance"
                  value={(draft.tags ?? []).join(", ")}
                  onChange={(value) => setDraft((current) => ({ ...current, tags: value.split(",").map((tag) => tag.trim()).filter(Boolean) }))}
                />
              </div>
            ) : null}
            {draft.type === "Current action" ? (
              <EditorField
                label="Public status label"
                placeholder="e.g. Drafting, Active, Closed"
                value={draft.actionStatus ?? ""}
                onChange={(value) => setDraft((current) => ({ ...current, actionStatus: value || null }))}
              />
            ) : null}
            <label>
              <span className="mb-1 block text-sm font-semibold text-secondary">Summary</span>
              <textarea className="min-h-24 w-full rounded-xl border border-line bg-surface px-4 py-3 text-base leading-7 text-ink outline-none focus:border-primary" value={draft.summary} onChange={(event) => setDraft((current) => ({ ...current, summary: event.target.value }))} />
            </label>
            <label>
              <span className="mb-1 block text-sm font-semibold text-secondary">Approval note</span>
              <input className="min-h-12 w-full rounded-xl border border-line bg-surface px-4 text-base text-ink outline-none focus:border-primary" value={approvalNote} onChange={(event) => setApprovalNote(event.target.value)} placeholder="Short note for the superadministrator reviewer." />
            </label>
            <RichTextEditor
              label="Body copy"
              value={draft.body}
              onChange={(value) => setDraft((current) => ({ ...current, body: value }))}
              placeholder="Use headings, short paragraphs, links and lists to shape the public page."
            />
          </div>
        </main>
        ) : null}

        {previewOpen ? (
          <aside className="relative min-w-0 bg-app p-5 md:p-8">
            {!fullPreview ? (
              <button
                type="button"
                className="absolute left-0 top-0 hidden h-full w-3 -translate-x-1/2 cursor-col-resize items-center justify-center xl:flex"
                onPointerDown={(event) => {
                  event.preventDefault();
                  resizeStart.current = { x: event.clientX, width: editorWidth };
                  setResizingPreview(true);
                }}
                onDoubleClick={() => setEditorWidth(defaultEditorWidth)}
                aria-label="Resize editor and preview"
                title="Drag to resize · double-click to reset"
              >
                <span className={`h-16 w-1 rounded-full ${resizingPreview ? "bg-primary" : "bg-line"}`} />
              </button>
            ) : null}
            <div className={fullPreview ? "" : "sticky top-20"}>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="mono text-[12px] uppercase tracking-[0.18em] text-secondary">Styled preview</div>
                  <div className="mt-1 hidden text-xs text-muted xl:block">{fullPreview ? "Full-width preview — the form is hidden." : "Drag the left edge to resize · double-click to reset."}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5 rounded-lg border border-line bg-canvas p-0.5" role="group" aria-label="Preview width">
                    {previewDeviceOptions.map((option) => (
                      <button key={option.key} type="button" onClick={() => setPreviewDevice(option.key)} aria-label={option.label} title={option.label} aria-pressed={previewDevice === option.key} className={`grid h-8 w-8 place-items-center rounded-md ${previewDevice === option.key ? "bg-surface text-primary shadow-[0_1px_0_rgba(11,31,58,0.04)]" : "text-secondary hover:text-ink"}`}>
                        <option.icon className="h-4 w-4" />
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={() => setFullPreview((open) => !open)} aria-label={fullPreview ? "Exit full-width preview" : "Full-width preview"} title={fullPreview ? "Exit full-width preview" : "Full-width preview"} aria-pressed={fullPreview} className="grid h-9 w-9 place-items-center rounded-lg border border-line bg-surface text-secondary hover:bg-hover hover:text-ink">
                    {fullPreview ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </button>
                  <button type="button" className="grid h-9 w-9 place-items-center rounded-lg border border-line bg-surface text-secondary hover:bg-hover hover:text-ink" onClick={() => { setPreviewOpen(false); setFullPreview(false); }} aria-label="Close preview">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="mx-auto w-full transition-[max-width] duration-200" style={previewDeviceWidths[previewDevice] ? { maxWidth: `${previewDeviceWidths[previewDevice]}px` } : undefined}>
                <PublicContentPreview item={draft} />
              </div>
              <a className="mono mt-5 block truncate text-sm text-secondary hover:text-primary" href={previewUrl} target="_blank" rel="noreferrer">
                lexmosaic.eu{draft.path === "/" ? "" : draft.path}
              </a>
            </div>
          </aside>
        ) : null}
      </div>

      <section className="grid gap-4 border-t border-line bg-surface px-5 py-5 md:px-8 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.8fr)]">
        <div>
          <div className="mb-3 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-ink">Approval queue</h2>
          </div>
          <div className="grid gap-3">
            {pendingChanges.length === 0 ? (
              <p className="rounded-xl border border-dashed border-line px-4 py-6 text-center text-sm text-secondary">Nothing waiting for approval right now.</p>
            ) : pendingChanges.map((change) => (
              <div key={change.id} className="rounded-xl border border-line bg-surfaceAlt p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold">{change.title}</h3>
                      <Tag>{change.type}</Tag>
                    </div>
                    <div className="mt-1 text-xs text-muted">Submitted by {change.submittedBy} on {new Date(change.submittedAt).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      title={canApprove ? "Return for revision" : "Only a superadministrator can review"}
                      aria-label="Return for revision"
                      disabled={!canApprove}
                      onClick={() => reviewChange(change.id, "rejected")}
                      className="grid h-9 w-9 place-items-center rounded-lg border border-line bg-surface text-secondary hover:bg-hover hover:text-ink disabled:pointer-events-none disabled:opacity-40"
                    >
                      <Undo2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title={canApprove ? "Approve and publish" : "Only a superadministrator can review"}
                      aria-label="Approve and publish"
                      disabled={!canApprove}
                      onClick={() => reviewChange(change.id, "approved")}
                      className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-white shadow-[0_4px_12px_rgba(122,28,44,0.22)] hover:brightness-95 disabled:pointer-events-none disabled:opacity-40"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {change.note ? <p className="mt-2 text-sm leading-6 text-secondary">{change.note}</p> : null}
                <PublicChangeDiff before={change.before} after={change.after} />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-line bg-surfaceAlt p-4">
          <div className="flex items-start gap-3">
            <Image className="mt-1 h-5 w-5 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">Public media</div>
              <p className="mt-2 text-sm leading-6 text-secondary">Select an approved public image for the page or insert it into the body copy.</p>
            </div>
          </div>
          <div className="mt-4 grid gap-3">
            {mediaItems.map((media) => (
              <div key={media.id} className="grid gap-3 rounded-lg border border-divider bg-surface p-3 sm:grid-cols-[96px_minmax(0,1fr)]">
                <div className="aspect-[4/3] overflow-hidden rounded-md bg-canvas">
                  <img src={media.url} alt={media.alt} className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold">{media.label}</div>
                  <div className="mono mt-1 truncate text-xs text-muted">{media.url}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" variant="secondary" onClick={() => setDraft((current) => ({ ...current, imageUrl: media.url }))}>Use as lead</Button>
                    <Button type="button" variant="secondary" onClick={() => setDraft((current) => ({ ...current, body: `${current.body}\n\n![${media.alt}](${media.url})` }))}>Insert</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-lg border border-divider bg-surface p-3">
            <div className="font-semibold">Add media by URL</div>
            <div className="mt-3 grid gap-2">
              <input className="min-h-10 rounded-lg border border-line bg-surface px-3 text-sm" value={mediaDraft.label} onChange={(event) => setMediaDraft((current) => ({ ...current, label: event.target.value }))} placeholder="Label" />
              <input className="min-h-10 rounded-lg border border-line bg-surface px-3 text-sm" value={mediaDraft.url} onChange={(event) => setMediaDraft((current) => ({ ...current, url: event.target.value }))} placeholder="https://lexmosaic.eu/images/example.png" />
              <input className="min-h-10 rounded-lg border border-line bg-surface px-3 text-sm" value={mediaDraft.alt} onChange={(event) => setMediaDraft((current) => ({ ...current, alt: event.target.value }))} placeholder="Alternative text" />
              <Button type="button" variant="secondary" onClick={async () => {
                try {
                  await persistence?.addMedia?.(mediaDraft);
                  setMediaDraft({ label: "", url: "", alt: "" });
                  onNotice("Public media asset saved.");
                } catch (error) {
                  onNotice(`Media asset was not saved: ${errorMessage(error)}`);
                }
              }}>Add media</Button>
            </div>
          </div>
          <div className="mt-5">
            <div className="font-semibold">Approval history</div>
            <div className="mt-2 grid gap-2">
              {changes.filter((change) => change.status !== "pending").map((change) => (
                <div key={change.id} className="flex items-center gap-2 rounded-lg border border-divider bg-surface px-3 py-2 text-sm">
                  {change.status === "approved" ? <Check className="h-4 w-4 shrink-0 text-emerald-700" /> : <Undo2 className="h-4 w-4 shrink-0 text-secondary" />}
                  <span className="min-w-0 truncate">
                    <span className="font-medium">{change.title}</span> <span className="text-secondary">{change.status}</span>
                    {change.reviewedAt ? <span className="text-muted"> · {new Date(change.reviewedAt).toLocaleString()}</span> : null}
                  </span>
                </div>
              ))}
              {changes.every((change) => change.status === "pending") ? <p className="text-sm text-secondary">No completed approvals yet.</p> : null}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function clampEditorWidth(value: number) {
  return Math.round(Math.min(720, Math.max(340, value)));
}

function readEditorWidth() {
  if (typeof window === "undefined") return defaultEditorWidth;
  const stored = Number(window.localStorage.getItem(editorWidthStorageKey));
  return Number.isFinite(stored) && stored > 0 ? clampEditorWidth(stored) : defaultEditorWidth;
}

function fromServerContent(item: PublicSiteEditorData["items"][number]): PublicContentItem {
  return {
    id: item.itemKey,
    itemKey: item.itemKey,
    type: item.contentType,
    title: item.title,
    slug: item.slug,
    path: item.path,
    summary: item.summary,
    body: item.body,
    imageUrl: item.imageUrl ?? undefined,
    status: item.status,
    updatedAt: item.updatedAt,
    tags: item.tags ?? [],
    publicDate: item.publicDate ?? null,
    actionStatus: item.actionStatus ?? null,
    lastPublishedAt: item.lastPublishedAt ?? null,
    publishError: item.publishError ?? null,
  };
}

function fromServerChange(change: PublicSiteEditorData["changes"][number]): PublicSiteChange {
  const before = parseContentSnapshot(change.beforeJson);
  const after = parseContentSnapshot(change.afterJson);
  return {
    id: change.id,
    itemId: after.itemKey || change.itemKey,
    title: change.title,
    type: change.contentType,
    submittedBy: change.submittedBy,
    submittedAt: change.submittedAt,
    status: change.status,
    note: change.note,
    before,
    after,
    reviewedAt: change.reviewedAt ?? undefined,
  };
}

function parseContentSnapshot(json: string): PublicContentItem {
  try {
    const parsed = JSON.parse(json) as Partial<PublicContentItem> & { contentType?: PublicContentType };
    return {
      id: parsed.itemKey ?? parsed.id ?? "public-content",
      itemKey: parsed.itemKey ?? parsed.id ?? "public-content",
      type: parsed.type ?? parsed.contentType ?? "Page",
      title: parsed.title ?? "Untitled",
      slug: parsed.slug ?? "untitled",
      path: parsed.path ?? "/",
      summary: parsed.summary ?? "",
      body: parsed.body ?? "",
      imageUrl: parsed.imageUrl ?? undefined,
      status: parsed.status ?? "draft",
      updatedAt: parsed.updatedAt ?? Date.now(),
      tags: parsed.tags ?? [],
      publicDate: parsed.publicDate ?? null,
      actionStatus: parsed.actionStatus ?? null,
    };
  } catch {
    return initialPublicContent[0];
  }
}

function errorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Unknown error.";
}

function PublishingMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-divider bg-canvas/60 px-3.5 py-2.5">
      <div className="mono text-[10px] uppercase tracking-[0.16em] text-secondary">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums text-ink">{value}</div>
    </div>
  );
}

function ContentTypeIcon({ type }: { type: PublicContentType }) {
  const className = "h-4 w-4 text-secondary";
  if (type === "Current action") return <Flag className={className} />;
  if (type === "News") return <FileText className={className} />;
  return <Layers className={className} />;
}

function StatusPill({ status }: { status: PublicContentStatus }) {
  const styles = status === "published"
    ? "bg-emerald-100 text-emerald-800"
    : status === "pending approval"
      ? "bg-rose-100 text-primary"
      : "bg-amber-100 text-amber-800";
  const label = status === "pending approval" ? "In review" : status[0].toUpperCase() + status.slice(1);
  return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${styles}`}>{label}</span>;
}

function PublishStatusIndicator({ item, onRetry }: { item: PublicContentItem; onRetry: () => void }) {
  if (item.status !== "published") return null;
  if (item.publishError) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-primary">
        Publish failed
        <button type="button" onClick={onRetry} className="underline hover:no-underline">Retry</button>
      </span>
    );
  }
  if (item.lastPublishedAt) {
    return <span className="text-xs text-secondary">Published to GitHub · {new Date(item.lastPublishedAt).toLocaleString()}</span>;
  }
  return null;
}

function EditorField({ label, value, onChange, emphasis = false, type = "text", placeholder }: { label: string; value: string; onChange: (value: string) => void; emphasis?: boolean; type?: string; placeholder?: string }) {
  return (
    <label>
      <span className="mb-1 block text-sm font-semibold text-secondary">{label}</span>
      <input type={type} placeholder={placeholder} className={`min-h-14 w-full rounded-xl border border-line bg-surface px-4 text-ink outline-none focus:border-primary ${emphasis ? "text-2xl font-semibold tracking-[-0.02em]" : "text-base"}`} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function EditorSelect({ label, value, values, onChange }: { label: string; value: string; values: string[]; onChange: (value: string) => void }) {
  return (
    <label>
      <span className="mb-1 block text-sm font-semibold text-secondary">{label}</span>
      <span className="relative block">
        <select className="min-h-14 w-full appearance-none rounded-xl border border-line bg-surface px-4 pr-10 text-lg font-semibold text-ink outline-none focus:border-primary" value={value} onChange={(event) => onChange(event.target.value)}>
          {values.map((item) => <option key={item}>{item}</option>)}
        </select>
        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary" />
      </span>
    </label>
  );
}

function PublicContentPreview({ item }: { item: PublicContentItem }) {
  return (
    <article className="overflow-hidden rounded-2xl border border-line bg-white shadow-[0_18px_50px_rgba(11,31,58,0.10)]">
      <div className="h-3 bg-primary" />
      <div className="p-7">
        <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">{item.type}</div>
        <h1 className="text-4xl font-semibold leading-tight tracking-[-0.02em] text-ink">{item.title || "Untitled page"}</h1>
        {item.summary ? <p className="mt-5 text-xl leading-8 text-secondary">{item.summary}</p> : null}
        {item.imageUrl ? <img src={item.imageUrl} alt="" className="mt-6 aspect-[16/9] w-full rounded-xl object-cover" /> : null}
        <div className="mt-7 border-t border-divider pt-6">
          <div className="grid gap-4 text-[16px] leading-8 text-ink">
            {renderMarkdownPreview(item.body)}
          </div>
        </div>
      </div>
    </article>
  );
}

function renderMarkdownPreview(markdown: string) {
  const lines = markdown.split(/\r?\n/);
  const nodes: JSX.Element[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    const items = listItems;
    listItems = [];
    nodes.push(
      <ul key={`list-${nodes.length}`} className="list-disc space-y-1 pl-5">
        {items.map((item, index) => <li key={`${item}-${index}`}>{renderInlineMarkdown(item)}</li>)}
      </ul>,
    );
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }
    if (trimmed.startsWith("- ")) {
      listItems.push(trimmed.slice(2));
      return;
    }
    flushList();
    const imageMatch = trimmed.match(/^!\[([^\]]*)\]\((https?:\/\/[^)]+)\)$/);
    if (imageMatch) {
      nodes.push(<img key={index} src={imageMatch[2]} alt={imageMatch[1]} className="aspect-[16/9] w-full rounded-xl object-cover" />);
      return;
    }
    if (trimmed.startsWith("### ")) {
      nodes.push(<h3 key={index} className="pt-3 text-xl font-semibold leading-snug tracking-[-0.01em] text-ink">{renderInlineMarkdown(trimmed.slice(4))}</h3>);
      return;
    }
    if (trimmed.startsWith("## ")) {
      nodes.push(<h2 key={index} className="pt-4 text-2xl font-semibold leading-tight tracking-[-0.01em] text-ink">{renderInlineMarkdown(trimmed.slice(3))}</h2>);
      return;
    }
    if (trimmed.startsWith("> ")) {
      nodes.push(<blockquote key={index} className="border-l-4 border-primary/40 bg-canvas px-4 py-3 text-ink">{renderInlineMarkdown(trimmed.slice(2))}</blockquote>);
      return;
    }
    nodes.push(<p key={index}>{renderInlineMarkdown(trimmed)}</p>);
  });
  flushList();
  return nodes.length > 0 ? nodes : <p className="text-muted">Start writing to preview the public page body.</p>;
}

function PublicChangeDiff({ before, after }: { before: PublicContentItem; after: PublicContentItem }) {
  const rows = [
    ["Title", before.title, after.title],
    ["Summary", before.summary, after.summary],
    ["Path", before.path, after.path],
    ["Lead image", before.imageUrl ?? "", after.imageUrl ?? ""],
    ["Body", before.body, after.body],
  ].filter(([, previous, next]) => previous !== next);
  if (rows.length === 0) return <p className="mt-3 text-sm text-secondary">No text changes detected.</p>;
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-divider">
      <div className="grid grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)] bg-canvas px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted">
        <span>Field</span>
        <span>Before</span>
        <span>After</span>
      </div>
      {rows.map(([label, previous, next]) => (
        <div key={label} className="grid grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)] gap-3 border-t border-divider px-3 py-2 text-sm">
          <span className="font-medium text-ink">{label}</span>
          <span className="line-clamp-3 text-rose-800">{previous || "Empty"}</span>
          <span className="line-clamp-3 text-emerald-800">{next || "Empty"}</span>
        </div>
      ))}
    </div>
  );
}

function renderInlineMarkdown(text: string) {
  const parts: (string | JSX.Element)[] = [];
  const pattern = /(\[([^\]]+)\]\((https?:\/\/[^)]+)\)|\*\*([^*]+)\*\*|_([^_]+)_|<u>(.*?)<\/u>)/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    if (match.index > cursor) parts.push(text.slice(cursor, match.index));
    if (match[2] && match[3]) {
      parts.push(<a key={`${match.index}-link`} className="font-semibold text-primary underline decoration-primary/30 underline-offset-4" href={match[3]} target="_blank" rel="noreferrer">{match[2]}</a>);
    } else if (match[4]) {
      parts.push(<strong key={`${match.index}-strong`} className="font-semibold text-ink">{match[4]}</strong>);
    } else if (match[5]) {
      parts.push(<em key={`${match.index}-em`}>{match[5]}</em>);
    } else if (match[6]) {
      parts.push(<span key={`${match.index}-underline`} className="underline underline-offset-4">{match[6]}</span>);
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}
